import React from "react";
import useSpacing from '../../core/hooks/useSpacing';
import useBaseStyle from '../../core/hooks/useBaseStyle';
import useCharacterStyle from '../../core/hooks/useCharacterStyle';
import { getTextPositionClasses, resolveTextPosition } from '../../core/utils/characterStyleUtils';
import { transformColor } from '../../core/utils/generalUtils';
import AutoFitText from "./AutoFitText";

/**
 * Extract the horizontal half of a ``text_position`` value (e.g.
 * ``"middle-left"`` → ``"left"``) and normalize it into one of the
 * three values ``AutoFitText`` understands. ``"full"`` (text-justify
 * via ``getTextPositionClasses``) maps to ``"left"`` because there's
 * no meaningful transform-origin for justified text — anchoring it
 * to the left keeps the visual rhythm of justified copy intact.
 */
function getHorizontalAlign(textPosition) {
  if (!textPosition || typeof textPosition !== "string") return "center";
  const horizontal = textPosition.split("-")[1];
  if (horizontal === "left" || horizontal === "right") return horizontal;
  return "center";
}

function getOffsetCopyConfig(characterStyle) {
  const dir = characterStyle?.text_offset_copy;
  if (!dir || dir === "none") return null;

  const distance = Number(characterStyle?.text_offset_copy_distance);
  const d = Number.isFinite(distance) ? distance : 7;
  const scaleRaw = Number(characterStyle?.text_offset_copy_scale);
  const scale = Number.isFinite(scaleRaw) ? Math.max(0.1, Math.min(1, scaleRaw)) : 1;

  const map = {
    right: [d, 0],
    left: [-d, 0],
    top: [0, -d],
    bottom: [0, d],
    "top-right": [d, -d],
    "top-left": [-d, -d],
    "bottom-right": [d, d],
    "bottom-left": [-d, d],
  };
  const [dx, dy] = map[dir] || [d, d];

  const textHex = transformColor(characterStyle?.text_color || "#111827", 100, "hex");
  const copyColor = characterStyle?.text_offset_copy_color
    ? transformColor(characterStyle.text_offset_copy_color, 100, "rgba")
    : transformColor(textHex, 35, "rgba");

  const connect = Boolean(characterStyle?.text_offset_copy_connect);
  return { dx, dy, scale, copyColor, connect };
}

function buildOffsetTrailLayers(offsetCopy) {
  if (!offsetCopy) return [];
  const { dx, dy, scale, connect } = offsetCopy;
  if (!connect) return [{ dx, dy, scale }];

  const dist = Math.hypot(dx, dy);
  // More distance -> more intermediate layers, capped to avoid huge DOM.
  // Smaller step size makes the trail smoother for large distances.
  // Connected trail density uses a logistic function of distance + size so:
  // - Shorter distances get more repetitions (denser trail for small offsets)
  // - Smaller sizes (scale closer to 0.1) get more repetitions
  //
  // x = (1 / max(distance, 3)) * max((1 - size) * 10, 1)
  // densityMultiplier = 1 + 10 * sigmoid(2 * (x - 1))   // ~1..11
  const distance = Math.max(dist, 0);
  const size = Number.isFinite(scale) ? scale : 1;
  const x = (1 / Math.max(distance, 3)) * Math.max((1 - size) * 10, 1);
  const sigmoid = 1 / (1 + Math.exp(-2 * (x - 1)));
  const densityMultiplier = 1 + 10 * sigmoid;

  const baseStepPx = dist >= 140 ? 1 : dist >= 60 ? 1.5 : 2;
  const stepPx = baseStepPx / densityMultiplier;
  // Extra repetitions for very short offsets or small sizes.
  const bonusConnections = dist < 4 || size < 0.7 ? 12 : 0;
  const steps = Math.max(2, Math.min(520, Math.ceil(dist / stepPx) + bonusConnections));
  const layers = [];
  for (let i = 1; i <= steps; i += 1) {
    const t = i / steps;
    layers.push({
      dx: dx * t,
      dy: dy * t,
      scale: 1 + (scale - 1) * t,
    });
  }
  return layers;
}

const TitleItem = ({
  item,
  returnDiv = true,
  ignoreSpacing = false,
  textAlign = 'center',
  reserveDescenderSpace = false,
}) => {
  const { combinedClasses } = useSpacing(item, ignoreSpacing);
  const { styleClasses: baseStyleClasses, inlineStyles: baseInlineStyles } =
    useBaseStyle(item);

  // Use the characterStyle hook to get character styles
  const { styleClasses: charStyleClasses, inlineStyles: charInlineStyles } =
    useCharacterStyle(item);

  // Handle null/undefined items gracefully
  if (!item || !item.text) {
    return null;
  }

  const charStyle = item.character_style || item.characterStyle;
  const offsetCopy = getOffsetCopyConfig(charStyle);
  const offsetLayers = buildOffsetTrailLayers(offsetCopy);

  // Get text position for container and heading separately
  const containerPositionClasses = getTextPositionClasses(
    item.character_style?.text_position || item.characterStyle?.text_position,
    true  // isContainer = true
  );
  const headingPositionClasses = getTextPositionClasses(
    item.character_style?.text_position || item.characterStyle?.text_position,
    false // isContainer = false
  );

  // Remove positioning classes from characterStyleClasses since we handle them separately
  const cleanCharacterClasses = charStyleClasses
    .split(" ")
    .filter((c) => !/^(flex|justify-|items-|text-left|text-right|text-center|text-justify)/.test(c))
    .join(" ");

  // Container gets spacing + container positioning classes
  const containerClasses = `${combinedClasses} ${containerPositionClasses}`.trim();

  // <h1> gets base style + clean character styles + heading text alignment
  const headingClasses = `${baseStyleClasses} ${cleanCharacterClasses} ${headingPositionClasses}`
    .split(" ")
    .filter(c => c && c.trim() !== "" && c.trim() !== "none")
    .join(" ");

  // Merge inline styles, but *combine* text-shadow instead of letting CharacterStyle overwrite BaseStyle.
  const mergedTextShadow = [baseInlineStyles?.textShadow, charInlineStyles?.textShadow]
    .filter(Boolean)
    .join(", ");
  const finalStyle = {
    ...baseInlineStyles,
    ...charInlineStyles,
    ...(mergedTextShadow ? { textShadow: mergedTextShadow } : {}),
  };
  // Tight line-heights can crop ascenders/descenders inside constrained containers.
  const headingStyle = reserveDescenderSpace
    ? { ...finalStyle, paddingTop: "0.15em", paddingBottom: "0.15em" }
    : finalStyle;

  if (!returnDiv) {
    // Inline rendering (used in NavBar and Hero): respect textAlign prop
    const cleanedClasses = headingClasses
      .split(" ")
      .filter(c => !c.startsWith("text-left") && !c.startsWith("text-right") && !c.startsWith("text-justify") && !c.startsWith("text-center"))
      .join(" ");
    
    // Map textAlign prop to Tailwind class
    const alignmentClass = textAlign === 'left' ? 'text-left' : textAlign === 'right' ? 'text-right' : 'text-center';
    const finalHeadingClasses = `${cleanedClasses} ${alignmentClass}`.trim();
    
    return (
      <h1 className={finalHeadingClasses} style={headingStyle}>
        {offsetCopy ? (
          <span className="relative inline-block">
            {offsetLayers.map((layer, idx) => (
              <span
                key={idx}
                aria-hidden="true"
                className="absolute left-0 top-0 select-none pointer-events-none"
                style={{
                  ...finalStyle,
                  color: offsetCopy.copyColor,
                  textShadow: "none",
                  // Ensure the offset layer does NOT inherit text border/stroke from BaseStyle.
                  "--pc-text-stroke-width": "0px",
                  "--pc-text-stroke-color": "transparent",
                  transform: `translate(${layer.dx}px, ${layer.dy}px) scale(${layer.scale})`,
                  transformOrigin: "top center",
                  zIndex: 0,
                }}
              >
                {item.text}
              </span>
            ))}
            <span className="relative" style={{ zIndex: 1 }}>
              {item.text}
            </span>
          </span>
        ) : (
          item.text
        )}
      </h1>
    );
  }

  // Stand-alone usage: wrap in container with spacing and positioning for proper layout
  // IMPORTANT: do NOT force `w-full` here. When horizontal margins are applied, `w-full`
  // would cause the element to overflow and appear shifted to the right (right side
  // gets clipped by overflow-hidden containers). As a block-level flex container,
  // width defaults to `auto` which correctly shrinks to account for margins and
  // keeps the element visually centered.

  // The ``<h1>`` markup is identical between the shrink-to-fit and
  // standard branches — only the wrapping changes. Lifted into a const
  // so the two return paths stay in lockstep when offset-copy or other
  // h1 internals evolve.
  const headingNode = (
    <h1 className={headingClasses} style={headingStyle}>
      {offsetCopy ? (
        <span className="relative inline-block">
          {offsetLayers.map((layer, idx) => (
            <span
              key={idx}
              aria-hidden="true"
              className="absolute left-0 top-0 select-none pointer-events-none"
              style={{
                ...finalStyle,
                color: offsetCopy.copyColor,
                textShadow: "none",
                // Ensure the offset layer does NOT inherit text border/stroke from BaseStyle.
                "--pc-text-stroke-width": "0px",
                "--pc-text-stroke-color": "transparent",
                transform: `translate(${layer.dx}px, ${layer.dy}px) scale(${layer.scale})`,
                transformOrigin: "top center",
                zIndex: 0,
              }}
            >
              {item.text}
            </span>
          ))}
          <span className="relative" style={{ zIndex: 1 }}>
            {item.text}
          </span>
        </span>
      ) : (
        item.text
      )}
    </h1>
  );

  // ``shrink_to_fit`` (model field on Title): when enabled, the title
  // auto-scales down to fit on a single line within the available
  // container width. Implementation lives in ``AutoFitText`` (CSS
  // ``transform: scale()``) — sidesteps the CSS specificity fight
  // that broke earlier ``font-size``-based attempts inside hero
  // automatic modes (the inner ``<h1>``'s ``text-[Npx]`` Tailwind
  // class always won the cascade, leaving no visible shrinking).
  //
  // Alignment for the transform-origin is derived from
  // ``character_style.text_position`` so the title shrinks "toward"
  // the same edge it's anchored to (e.g. a bottom-left-anchored title
  // shrinks toward its left edge, not its center).
  if (item.shrink_to_fit) {
    const rawTextPosition =
      item.character_style?.text_position ||
      item.characterStyle?.text_position;
    // ``text_position`` may now be a responsive wrapper object — resolve it
    // to the matching string for the current viewport before extracting the
    // horizontal axis. Falls back to the string code path when callers (or
    // legacy tests) still pass a plain string.
    const textPosition = resolveTextPosition(rawTextPosition);
    const fitAlign = getHorizontalAlign(textPosition);
    return (
      <div className={containerClasses}>
        <AutoFitText align={fitAlign}>{headingNode}</AutoFitText>
      </div>
    );
  }

  return <div className={containerClasses}>{headingNode}</div>;
};

export default TitleItem;
